<template>
    <div v-if="errorHandler.isVisible"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
        <div class="relative w-full max-w-md p-6 bg-black rounded-xl shadow-lg text-white animate-fade-in-scale">
            <!-- Large Icon -->
            <div class="flex justify-center -mt-16">
                <div class="flex items-center justify-center w-24 h-24 bg-red-600 rounded-full shadow-lg">
                    <font-awesome-icon v-if="iconForErrorCode" :icon="iconForErrorCode" class="text-4xl text-white" />
                </div>
            </div>

            <!-- Error Header -->
            <h2 class="mt-6 text-xl font-bold text-center">
                {{ translatedHeader }}
            </h2>

            <!-- Error Description -->
            <p class="mt-4 text-center text-gray-400">
                {{ translatedDescription }}
            </p>

            <!-- Action Buttons -->
            <div class="mt-6 flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3 justify-center">
                <button v-if="errorHandler.errorCode === 429" @click="handleRetry"
                    class="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2">
                    <font-awesome-icon icon="redo" class="mr-2" />
                    {{ translatedButtons.retry }}
                </button>
                <button v-else-if="errorHandler.errorCode === 403" @click="redirectToLogin"
                    class="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2">
                    <font-awesome-icon icon="sign-in-alt" class="mr-2" />
                    {{ translatedButtons.login }}
                </button>
                <button v-else-if="errorHandler.errorCode === 500" @click="handleRetry"
                    class="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2">
                    <font-awesome-icon icon="redo" class="mr-2" />
                    {{ translatedButtons.retry }}
                </button>
                <!-- New condition for 404 error -->
                <button v-else-if="errorHandler.errorCode === 404" @click="redirectToHome"
                    class="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2">
                    <font-awesome-icon icon="home" class="mr-2" />
                    {{ translatedButtons.home }}
                </button>
                <!-- Existing default button -->
                <button v-else @click="handleClose"
                    class="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2">
                    <font-awesome-icon icon="times" class="mr-2" />
                    {{ translatedButtons.close }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, watch, onMounted, onBeforeUnmount, computed } from "vue";
import { useRouter } from "vue-router";
import { translate } from "@/utils/translate";
import { errorHandler, clearError } from "@/utils/errorHandler";

export default {
    name: "ErrorHandler",
    setup() {
        const router = useRouter();
        const countdown = ref(0);
        let intervalId = null;

        const translatedHeader = computed(() => {
            if (errorHandler.errorCode) {
                return translate(`errors.${errorHandler.errorCode}.header`, {});
            }
            return translate("errors.default.header", {});
        });

        const translatedDescription = computed(() => {
            if (errorHandler.errorCode) {
                return translate(`errors.${errorHandler.errorCode}.description`, {
                    countdown: countdown.value,
                });
            }
            return translate("errors.default.description", {});
        });

        const translatedButtons = computed(() => ({
            close: translate("buttons.close"),
            login: translate("buttons.login"),
            retry: translate("buttons.retry"),
            home: translate("buttons.home"), // Added 'home' button translation
        }));

        // Icon mapping based on error codes
        const iconForErrorCode = computed(() => {
            switch (errorHandler.errorCode) {
                case 429:
                    return "exclamation-triangle";
                case 403:
                    return "ban";
                case 500:
                    return "server";
                case 404:
                    return "home"; // Optional: choose a different icon if desired
                default:
                    return "info-circle";
            }
        });

        const handleClose = () => {
            clearError();
        };

        const handleRetry = () => {
            clearError();
            window.location.reload();
        };

        const redirectToLogin = () => {
            clearError();
            router.push({
                name: "Login",
                query: { redirect: router.currentRoute.value.fullPath },
            });
        };

        // New method to redirect to the homepage
        const redirectToHome = () => {
            clearError();
            router.push({ name: "EventList" }); // Ensure 'Home' matches your route name
        };

        const startCountdown = () => {
            if (errorHandler.errorCode === 429 && errorHandler.waitTime > 0) {
                countdown.value = errorHandler.waitTime;
                intervalId = setInterval(() => {
                    if (countdown.value > 0) {
                        countdown.value -= 1;
                    } else {
                        clearInterval(intervalId);
                        window.location.reload();
                    }
                }, 1000);
            }
        };

        watch(
            () => errorHandler.isVisible,
            (isVisible) => {
                if (isVisible) {
                    startCountdown();
                } else if (intervalId) {
                    clearInterval(intervalId);
                    intervalId = null;
                }
            }
        );

        onMounted(() => {
            if (errorHandler.isVisible) {
                startCountdown();
            }
        });

        onBeforeUnmount(() => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        });

        return {
            errorHandler,
            countdown,
            translatedHeader,
            translatedDescription,
            translatedButtons,
            iconForErrorCode,
            handleClose,
            handleRetry,
            redirectToLogin,
            redirectToHome, // Expose the new method
        };
    },
};
</script>

<style scoped>
@keyframes fade-in-scale {
    from {
        opacity: 0;
        transform: scale(0.9);
    }

    to {
        opacity: 1;
        transform: scale(1);
    }
}

.animate-fade-in-scale {
    animation: fade-in-scale 0.3s ease-out;
}
</style>